/**
 * Segmented control.
 *
 * Two or three exclusive choices, both visible, no popover. It exists for one
 * control in particular, the Simple and Advanced switch on S20, and the reason
 * that control is a segment rather than a checkbox is the promise underneath
 * it: Advanced is not "more stuff turned on", it is a second reading of the
 * same screen, and two labelled halves say that where a single toggle says
 * "off and on".
 *
 * It is a radio group, not a row of buttons. The distinction is not pedantry:
 * a keyboard reaches the group once, arrows move between the options, and a
 * screen reader announces "Simple, selected, one of two" rather than reading
 * two unrelated buttons. The rail's own copy of this control predates the
 * pattern and should collapse onto it the next time the shell is opened.
 *
 * The selected segment is a surface step and a hairline, never the accent and
 * never a fill: this is a preference, not a status, and this product spends
 * saturation on state alone.
 */

import { useRef } from 'react';
import '../../styles/components/segmented-control.css';

export interface Segment<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  /** Names the group for a screen reader: "Level of detail". */
  label: string;
  value: T;
  options: Segment<T>[];
  onChange: (next: T) => void;
  /** Id of the paragraph that explains what the choice does. */
  describedBy?: string;
}

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  describedBy,
}: SegmentedControlProps<T>) {
  const groupRef = useRef<HTMLDivElement | null>(null);

  function move(delta: number) {
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + delta + options.length) % options.length];
    onChange(next.value);
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('.seg__option');
    buttons?.[options.indexOf(next)]?.focus();
  }

  return (
    <div
      className="seg"
      role="radiogroup"
      aria-label={label}
      aria-describedby={describedBy}
      ref={groupRef}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          move(1);
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className="seg__option"
            role="radio"
            aria-checked={selected}
            // Roving tabindex: the group is one tab stop, and the arrows do
            // the rest. Three tab stops for one preference is three stops a
            // keyboard user pays on every pass down the page.
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
